import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Package, Search } from "lucide-react";
import MerchItemForm, { EMPTY_MERCH, merchToForm } from "@/components/merch/MerchItemForm";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const stockLabel = (it) => {
  if (it.variants?.length) {
    return it.variants.map((v) => `${v.label}: ${v.stock == null ? "∞" : v.stock}`).join(" · ");
  }
  return `Stock: ${it.stock == null ? "∞" : it.stock}`;
};

function ShowPicker({ shows, selected, onSelect }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? shows.filter((s) => (s.title || "").toLowerCase().includes(n)) : shows;
  }, [shows, q]);
  return (
    <div className="surface-card p-4" data-testid="merch-show-picker">
      <div className="relative mb-3">
        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-dim)]" />
        <input placeholder="Find a show" value={q} onChange={(e) => setQ(e.target.value)} className="w-full pl-8 pr-3 py-2 text-sm" data-testid="merch-show-search" />
      </div>
      <div className="max-h-[480px] overflow-y-auto space-y-px">
        {list.map((s) => (
          <button key={s.id} onClick={() => onSelect(s.id)} data-testid={`merch-show-${s.id}`}
            className={`w-full text-left px-3 py-2 text-sm transition-colors ${selected === s.id ? "bg-[var(--ff-red)] text-white" : "hover:bg-[var(--ff-surface-hover)]"}`}>
            {s.title}
          </button>
        ))}
      </div>
    </div>
  );
}

function ItemRow({ it, active, onEdit, onDelete }) {
  return (
    <div className={`surface-card p-4 flex items-center gap-4 ${active ? "border-[var(--ff-red)]" : ""}`} data-testid={`merch-admin-row-${it.id}`}>
      {it.image_url
        ? <img src={it.image_url} alt={it.name} className="w-14 h-14 object-cover" />
        : <div className="w-14 h-14 bg-[var(--ff-bg)] flex items-center justify-center"><Package size={20} className="text-[var(--ff-text-dim)]" /></div>}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-lg">{it.name}</span>
          <span className="font-display text-[var(--ff-red)]">{money(it.price_cents)}</span>
          {!it.active && <span className="label-eyebrow text-yellow-300" data-testid={`merch-hidden-${it.id}`}>HIDDEN</span>}
        </div>
        <div className="text-xs text-[var(--ff-text-dim)] font-mono-ui">
          {it.requires_shipping ? "PHYSICAL" : "DIGITAL"} · {stockLabel(it)}
        </div>
      </div>
      <button onClick={() => onEdit(it)} className="text-[var(--ff-text-dim)] hover:text-white" data-testid={`merch-edit-${it.id}`}><Pencil size={14} /></button>
      <button onClick={() => onDelete(it)} className="text-[var(--ff-red)]" data-testid={`merch-delete-${it.id}`}><Trash2 size={14} /></button>
    </div>
  );
}

export default function MerchItemsAdmin() {
  const [shows, setShows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MERCH);

  useEffect(() => {
    api.get("/shows").then(({ data }) => {
      setShows(data || []);
      if (data?.length) setSelected(data[0].id);
    }).catch(() => toast.error("Failed to load shows"));
  }, []);

  const load = async () => {
    if (!selected) return;
    try { const { data } = await api.get(`/admin/shows/${selected}/merch`); setItems(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load items"); }
  };
  useEffect(() => { load(); reset(); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => { setEditing(null); setForm(EMPTY_MERCH); };
  const startEdit = (it) => { setEditing(it); setForm(merchToForm(it)); };
  const del = async (it) => {
    if (!window.confirm(`Delete "${it.name}"? Existing orders are kept.`)) return;
    try { await api.delete(`/admin/merch/${it.id}`); toast.success("Item deleted"); if (editing?.id === it.id) reset(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };
  const current = shows.find((s) => s.id === selected);

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="merch-items-admin">
      <div className="lg:col-span-3"><ShowPicker shows={shows} selected={selected} onSelect={setSelected} /></div>
      <div className="lg:col-span-4">
        {selected && <MerchItemForm showId={selected} editing={editing} form={form} setForm={setForm} onSaved={() => { reset(); load(); }} onCancel={reset} />}
      </div>
      <div className="lg:col-span-5 space-y-2">
        <div className="label-eyebrow mb-2" data-testid="merch-current-show">{current ? `${current.title} · ${items.length} ITEM${items.length === 1 ? "" : "S"}` : "—"}</div>
        {items.length === 0 && <div className="surface-card p-6 text-[var(--ff-text-dim)] italic" data-testid="merch-empty">No merch for this show yet.</div>}
        {items.map((it) => <ItemRow key={it.id} it={it} active={editing?.id === it.id} onEdit={startEdit} onDelete={del} />)}
      </div>
    </div>
  );
}
